import React, { useState, useEffect } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Container } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import route from '../../utils/route';
import CompanyModal from '../Modal/Modal';
import companyService from '../../services/company.service';
import NavigateSnackbar from '../navigateSnackbar/NavigateSnackbar';
import {
  InviteStatus, ProjectStatus, UserPermission, UserRole, UserStatus,
} from '../../utils/constants';
import { useCommonContext } from '../header/context';
import { CustomPagination, projectStyles, StyledTablePagination } from '../header/style';
import ActionModal from '../ActionModal/ActionModal';
import commonUtils from '../../utils/common/common';
import useMeta from '../../utils/useMeta';
import DynamicForm from '../Filters';
import DateRangeFilter from '../DateRangeFilter';

function Companies() {
  useMeta('Company List', 'Manage your company details and team access.');
  const navigate = useNavigate();
  const {
    user,
    controller,
    setController,
  } = useCommonContext();
  const [loading, setLoading] = useState(false);
  const [companydata, setCompanyData] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [opend, setOpend] = React.useState(false);
  const [eventId, setEventId] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [formData, setFormData] = useState({ companyName: '', companyStatus: UserStatus.STATUS });
  const [companyEdit, setCompanyEdit] = useState('');
  const [companyShow, setcompanyShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState();
  const [isDeleteCompany, setIsDeleteCompany] = useState(false);
  const [isBlock, setIsBlock] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [dateRange, setDateRange] = useState();

  const handleClose = () => {
    setOpen(false);
    setFormData({ companyName: '' });
    setcompanyShow(false);
    setCompanyEdit('');
  };
  const [snackbar, setSnackbar] = useState({
    state: false,
    navigateText: '',
    message: '',
    severity: 'success',
  });

  const classes = projectStyles();

  const [formValues, setFormValues] = useState({
    companyId: null,
    companyStatus: null,
  });

  const fields = [
    {
      key: 'companyId',
      type: 'dropdown',
      label: 'Company',
      value: formValues.companyId,
      options: companyList && companyList?.map((company) => ({
        value: company.companyId,
        label: company.companyName,
      })),
    },
    {
      key: 'companyStatus',
      type: 'dropdown',
      label: 'Status',
      value: formValues.companyStatus,
      options: Object.entries(ProjectStatus).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    },
  ];

  const handleApply = (range) => {
    setDateRange(range);
  };

  const handleFieldChange = (fieldKey, value) => {
    setFormValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSubmit = () => {
    setFilterValues(formValues);
  };

  const handleClear = () => {
    setFormValues({
      companyId: null,
      companyStatus: null,
    });
    setFilterValues({});
  };

  const generateQuery = (filterParams = {}) => {
    const timestring = 'createAt';
    let filterAnd = '';
    if (dateRange) {
      filterAnd = `${timestring}|gte|${dateRange.startDate}&${timestring}|lte|${dateRange.endDate}`;
    }

    const appendFilter = (condition) => {
      if (filterAnd) {
        filterAnd += `&${condition}`;
      } else {
        filterAnd += condition;
      }
    };

    if (filterParams.companyId) {
      appendFilter(`companyId|eq|${filterParams.companyId}`);
    }

    if (filterParams.companyName) {
      appendFilter(`companyName|lic|${filterParams.companyName}`);
    }

    if (filterParams.companyStatus) {
      appendFilter(`companyStatus|eq|${filterParams.companyStatus}`);
    }
    console.log(filterAnd);

    const params = {
      pageNumber: `${controller.page - 1}`,
      pageSize: `${controller.rowsPerPage}`,
      orders: `${timestring}|desc`,
      filterAnd,
    };
    return new URLSearchParams(params).toString();
  };
  const getAllCompany = async () => {
    try {
      setLoading(true);
      const query = generateQuery(filterValues);
      const res = await companyService.getAllCompany(query);
      if (res) {
        setCompanyData(res.content);
        setTotalPages(res.totalPages);
      }
    } catch (error) {
      setSnackbar({
        navigateText: '',
        severity: 'error',
        state: true,
        message: `${error?.data?.message}`,
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchCompanyList = async () => {
    try {
      const res = await companyService.getAll();
      setCompanyList(res);
    } catch (error) {
      setCompanyList([]);
    }
  };
  useEffect(() => {
    fetchCompanyList();
  }, []);
  useEffect(() => {
    getAllCompany();
  }, [controller, filterValues, dateRange]);

  const handleChangePage = (event, newPage) => {
    setController({ ...controller, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setController({ rowsPerPage: parseInt(event.target.value, 10), page: 1 });
  };

  const onHandleDelete = async () => {
    if (eventId) {
      try {
        await companyService.deleteCompany(eventId);
        setOpend(false);
        getAllCompany();
      } catch (error) {
        setOpend(false);
      } finally {
        setOpend(false);
      }
    }
  };

  const onDelete = async (id) => {
    if (id) {
      setOpend(true);
      setIsDeleteCompany(true);
      setIsBlock(false);
      setIsActive(false);
      setEventId(id);
    }
  };

  const onChange = (e) => {
    const { value } = e.target;
    if (companyShow) {
      setCompanyEdit({ ...companyEdit, companyName: value });
    } else {
      setFormData({ ...formData, companyName: commonUtils.sanitizeInput(value) });
    }
  };
  const handleSubmitForm = async () => {
    try {
      setLoading(true);
      await companyService.addCompany(formData);
      handleClose();
      getAllCompany();
      setSnackbar({
        navigateText: '',
        severity: 'success',
        state: true,
        message: 'Company Created ..!',
      });
      setTimeout(() => {
        navigate(`/${route.ROUTE_COMPANIES}`);
      }, 2000);
    } catch (err) {
      setSnackbar({
        navigateText: '',
        severity: 'error',
        state: true,
        message: `${err.data.message}`,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateForm = async () => {
    const { companyId, companyName } = companyEdit;
    try {
      setLoading(true);
      await companyService.updateCompany({ companyId, companyName });
      handleClose();
      getAllCompany();
    } catch (err) {
      setSnackbar({
        navigateText: '',
        severity: 'error',
        state: true,
        message: `${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };
  const editDetail = (item) => {
    setCompanyEdit(item);
    setcompanyShow(true);
    setOpen(true);
  };

  const onHandleBlock = async () => {
    if (eventId) {
      const payload = {
        companyId: eventId.companyId,
        status: eventId.companyStatus === InviteStatus.ACTIVE
          ? InviteStatus.BLOCKED : InviteStatus.ACTIVE,
      };
      try {
        setLoading(true);
        const response = await companyService.updateCompanyStatus(payload);
        getAllCompany();
        setSnackbar({
          navigateText: '',
          severity: 'success',
          state: true,
          message: `${response}`,
        });
        setOpend(false);
      } catch (error) {
        setSnackbar({
          navigateText: '',
          severity: 'error',
          state: true,
          message: `${error.message}`,
        });
        setOpend(false);
      } finally {
        setLoading(false);
        setOpend(false);
      }
    }
  };

  const blockCompany = async (item) => {
    if (item) {
      setOpend(true);
      setIsBlock(item.companyStatus === InviteStatus.ACTIVE);
      setIsActive(item.companyStatus === InviteStatus.BLOCKED);
      setIsDeleteCompany(false);
      setEventId(item);
      setCurrentStatus(item.companyStatus === InviteStatus.ACTIVE
        ? InviteStatus.BLOCK : InviteStatus.ACTIVE);
    }
  };

  const handleCloseModal = () => {
    setOpend(false);
    setFormData({ companyName: '', companyStatus: UserStatus.STATUS });
    setIsDeleteCompany(false);
    setIsBlock(false);
    setIsActive(false);
    setEventId(null);
    setCurrentStatus('');
  };

  return (
    <>
      <Container maxWidth={false} className={classes.root}>
        <CssBaseline />
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" marginBottom={2}>
          <Typography color="#666666" onClick={() => navigate(`/${route.ROUTE_DASHBOARD}`)} sx={{ cursor: 'pointer' }}>Dashboard</Typography>
          <Typography color="#666666">Company</Typography>
        </Breadcrumbs>
        <Box className={classes.heading}>
          <Typography className={classes.title} variant="h4">
            Companies
          </Typography>
          {user?.role?.userPermissionsList.includes(UserPermission.COMPANY) && <Button
            variant="contained"
            disabled={loading}
            className={classes.actionButton}
            onClick={() => setOpen(true)}
          >
            Create New Company
          </Button>}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} mt={4}>
            <Paper className={classes.summaryBox}>
              <Box className={classes.summaryBoxInTeam}>
                <Grid item xs={12}>
                  <Grid sx={{
                    py: 2,
                    px: 1.5,
                  }}
                  >
                    {user?.role?.roleName === UserRole.SUPER_ADMIN && <DateRangeFilter
                      initialDateRange={dateRange}
                      onApply={handleApply}
                    />}
                  </Grid>
                  <Grid
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={{
                      py: 2,
                      px: 1.5,
                      borderBottom: '1px solid #e2e5e8',
                    }}
                  >
                    <DynamicForm
                      fields={fields}
                      onChange={handleFieldChange}
                      onSubmit={handleSubmit}
                      onClear={handleClear}
                    />
                  </Grid>
                </Grid>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  {user?.role?.userPermissionsList.includes(UserPermission.COMPANY)
                    ? <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.headerCell}>Name</TableCell>
                          <TableCell className={classes.headerCell}>User Name</TableCell>
                          <TableCell className={classes.headerCell}>Email</TableCell>
                          <TableCell className={classes.headerCell}>Status</TableCell>
                          {user?.role?.userPermissionsList.includes(UserPermission.COMPANY)
                            && <TableCell className={classes.headerCell}>Action</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? <>
                          <TableRow>
                            <TableCell><Skeleton animation="wave" /></TableCell>
                            <TableCell><Skeleton animation="wave" /></TableCell>
                            <TableCell><Skeleton animation="wave" /></TableCell>
                            <TableCell><Skeleton animation="wave" /></TableCell>
                            <TableCell><Skeleton animation="wave" /></TableCell>
                          </TableRow>
                        </> : <>
                          {companydata?.length ? (
                            companydata.map((item) => (
                              <TableRow key={item.companyId}>
                                <TableCell>
                                  <Typography variant="body1">{item.companyName}</Typography>
                                </TableCell>
                                <TableCell>{item.userResponse.username}</TableCell>
                                <TableCell>{item.userResponse.email}</TableCell>
                                <TableCell>{item.companyStatus}</TableCell>
                                {user?.role?.userPermissionsList.includes(UserPermission.COMPANY)
                                  && (
                                    <TableCell>
                                      <Box display="flex" justifyContent="flex-start">
                                        <Tooltip title="Edit">
                                          <IconButton
                                            style={{ color: '#1D6AE5' }}
                                            disabled={item?.companyStatus === InviteStatus.BLOCKED}
                                            onClick={() => editDetail(item)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton
                                            color="error"
                                            onClick={() => onDelete(item?.companyId)}
                                            disabled={item?.companyStatus === InviteStatus.BLOCKED}
                                          >
                                            <CancelOutlinedIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={item.companyStatus === InviteStatus.ACTIVE ? 'Block' : 'Active'}
                                        >
                                          <IconButton
                                            style={{ color: '#FFC72B' }}
                                            onClick={() => blockCompany(item)}
                                          >
                                            {item.companyStatus === InviteStatus.ACTIVE ? (
                                              <BlockOutlinedIcon />
                                            ) : (
                                              <CheckCircleOutlineIcon />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  )}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                <Typography variant="body2" color="textSecondary">
                                  No companies found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </>}
                      </TableBody>
                    </Table> : <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.headerCell}>Name</TableCell>
                          <TableCell className={classes.headerCell}>User Name</TableCell>
                          <TableCell className={classes.headerCell}>Email</TableCell>
                          <TableCell className={classes.headerCell}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {companydata?.length ? companydata?.map((item) => (
                          <TableRow key={item?.companyId}>
                            <TableCell>
                              <Typography variant="body1">{item?.companyName}</Typography>
                            </TableCell>
                            <TableCell>{item?.userResponse?.username}</TableCell>
                            <TableCell>{item?.userResponse?.email}</TableCell>
                            <TableCell>{item?.companyStatus}</TableCell>
                          </TableRow>
                        )) : <TableRow>
                          <TableCell><Skeleton animation="wave" /></TableCell>
                          <TableCell><Skeleton animation="wave" /></TableCell>
                          <TableCell><Skeleton animation="wave" /></TableCell>
                        </TableRow>}
                      </TableBody>
                    </Table>}
                  {totalPages > 0 ? (
                    <Box className={classes.paginationContainer}>
                      <StyledTablePagination
                        count={totalPages * controller.rowsPerPage}
                        totalPages={totalPages}
                        page={controller.page - 1}
                        onPageChange={handleChangePage}
                        ActionsComponent={() => <CustomPagination
                          count={totalPages}
                          page={controller.page}
                          onChange={handleChangePage}
                          variant="outlined"
                          shape="rounded"
                          showFirstButton
                          showLastButton
                        />}
                        rowsPerPage={controller.rowsPerPage}
                        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  ) : <></>}
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        {opend && (
          <ActionModal
            handleClose={handleCloseModal}
            opend={opend}
            handleAction={isDeleteCompany ? onHandleDelete : onHandleBlock}
            isDelete={isDeleteCompany}
            isBlock={isBlock}
            isActive={isActive}
            action={currentStatus}
            page="company"
          />
        )}
        {open && (
          <CompanyModal
            open={open}
            handleClose={handleClose}
            data={formData}
            onChange={onChange}
            handleSubmitForm={handleSubmitForm}
            handleUpdateForm={handleUpdateForm}
            companyEdit={companyEdit}
            companyShow={companyShow}
          />
        )}
        {
          snackbar.state
          && (
            <NavigateSnackbar
              snackbarObj={snackbar}
              setSnackbar={setSnackbar}
            />
          )
        }
      </Container>
    </>
  );
}

export default Companies;
